import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { PaginationState } from '../../models';

/**
 * BrowsePaginator — Presentation Component
 *
 * Traditional page-number paginator matching the screenshot design.
 * Displays: < 1 2 3 ... 12 >
 *
 * Logic for computing visible page numbers is self-contained
 * since it's pure UI rendering logic (no business impact).
 */
@Component({
  selector: 'app-browse-paginator',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './browse-paginator.component.html',
  styleUrl: './browse-paginator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrowsePaginatorComponent {
  /** Pagination state from the facade */
  @Input({ required: true }) pagination!: PaginationState;

  /** Emitted when user clicks a page number */
  @Output() pageChange = new EventEmitter<number>();

  /** Maximum number of page buttons to show before truncating */
  private maxVisiblePages = 5;

  /**
   * Compute which page numbers to display.
   * Shows first, last, current, and adjacent pages with ellipsis gaps.
   * Returns: [1, 2, 3, -1, 12] where -1 represents ellipsis.
   */
  get visiblePages(): number[] {
    const { currentPage, totalPages } = this.pagination;
    if (totalPages <= this.maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: number[] = [];
    const half = Math.floor(this.maxVisiblePages / 2);

    let start = Math.max(2, currentPage - half);
    let end = Math.min(totalPages - 1, currentPage + half);

    // Adjust window if near edges
    if (currentPage <= half + 1) {
      end = this.maxVisiblePages - 1;
    }
    if (currentPage >= totalPages - half) {
      start = totalPages - this.maxVisiblePages + 2;
    }

    // Always show first page
    pages.push(1);

    // Ellipsis before middle pages
    if (start > 2) {
      pages.push(-1); // -1 = ellipsis marker
    }

    // Middle pages
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Ellipsis after middle pages
    if (end < totalPages - 1) {
      pages.push(-1);
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  }

  get canGoPrevious(): boolean {
    return this.pagination.currentPage > 1;
  }

  get canGoNext(): boolean {
    return this.pagination.currentPage < this.pagination.totalPages;
  }

  onPageClick(page: number): void {
    if (page !== -1 && page !== this.pagination.currentPage) {
      this.pageChange.emit(page);
    }
  }

  onPrevious(): void {
    if (this.canGoPrevious) {
      this.pageChange.emit(this.pagination.currentPage - 1);
    }
  }

  onNext(): void {
    if (this.canGoNext) {
      this.pageChange.emit(this.pagination.currentPage + 1);
    }
  }
}
